/**
 * Builds and sends the multipart POST that files a feedback ticket.
 *
 * Posts SAME-ORIGIN to `/api/feedback`, which the BFF forwards to leon-intake's
 * `POST /v1/feedback` with its own signed token. The browser sends the session
 * cookie plus the CSRF header; the BFF rebuilds `user` and `tenant` from the
 * session, so whatever identity this module puts in the payload is advisory.
 *
 * Two things this module does not compromise on:
 *
 *  1. Redaction: applied to `feedback`, every consoleErrors[].message,
 *     `client.ua` and `tenant.name` via redact()/redactEntries(); `route` goes
 *     through redactRoute() since a URL can carry a credential in its query
 *     string. Done HERE, in the browser, because the point of redaction is that
 *     the secret never leaves it.
 *  2. Toggle discipline: data the user switched off is not sent. That is
 *     enforced HERE against whatever the caller captured, so a stray
 *     screenshot blob or console entry cannot leak past a toggle that is off.
 *
 * It never surfaces a raw server error: every failure path normalizes to a
 * `FeedbackSubmitError` with a fixed, safe message.
 */
import { CSRF_HEADER } from '@/api/client';
import { endpoints } from './config';
import { withBase } from '@/lib/basePath';
import { redact, redactEntries, redactRoute, type ConsoleEntry } from './redact';
import { MESSAGES } from './strings';
import type { FeedbackTool } from './types';

export interface FeedbackClientInfo {
  viewport: string;
  ua: string;
  build: string | null;
}

export interface FeedbackIdentity {
  email: string;
  userId: string | number | null;
  role: string | null;
  tenantId: string | number | null;
  tenantName: string | null;
}

export interface SubmitFeedbackInput {
  tool: FeedbackTool;
  feedback: string;
  route: string;
  sessionSeconds: number;
  identity: FeedbackIdentity;
  client: FeedbackClientInfo;
  /** Whatever was captured; suppression against `includeConsole` happens inside. */
  consoleErrors: ConsoleEntry[];
  includeConsole: boolean;
  /** Whatever was captured; suppression against `includeScreenshot` happens inside. */
  screenshot: Blob | null;
  includeScreenshot: boolean;
  /** The session's CSRF token, sent as `x-billing-csrf`. */
  csrfToken: string | null;
}

export interface FeedbackSubmitResult {
  key: string;
  url: string;
  /** Set only when the issue was created but the screenshot attach failed. */
  warning?: string;
}

export class FeedbackSubmitError extends Error {
  constructor(message: string = MESSAGES.errorGeneric) {
    super(message);
    this.name = 'FeedbackSubmitError';
  }
}

/** Exported for tests: the exact payload this module would send, redaction and toggles applied. */
export function buildFeedbackPayload(input: SubmitFeedbackInput) {
  return {
    tool: input.tool,
    feedback: redact(input.feedback),
    route: redactRoute(input.route),
    sessionSeconds: input.sessionSeconds,
    user: {
      email: input.identity.email,
      id: input.identity.userId,
      role: input.identity.role,
    },
    tenant: {
      id: input.identity.tenantId,
      name: input.identity.tenantName === null ? null : redact(input.identity.tenantName),
    },
    client: {
      ...input.client,
      ua: redact(input.client.ua),
    },
    consoleErrors: input.includeConsole ? redactEntries(input.consoleErrors) : [],
    includeScreenshot: input.includeScreenshot,
    includeConsole: input.includeConsole,
  };
}

export async function submitFeedback(input: SubmitFeedbackInput): Promise<FeedbackSubmitResult> {
  const payload = buildFeedbackPayload(input);

  const form = new FormData();
  form.append('payload', JSON.stringify(payload));
  if (input.includeScreenshot && input.screenshot) {
    form.append('screenshot', input.screenshot, 'screenshot.png');
  }

  const headers: Record<string, string> = {};
  if (input.csrfToken) headers[CSRF_HEADER] = input.csrfToken;

  let response: Response;
  try {
    response = await fetch(withBase(endpoints.feedback), { method: 'POST', body: form, headers, credentials: 'same-origin' });
  } catch {
    throw new FeedbackSubmitError(MESSAGES.errorNetwork);
  }

  if (response.status === 503) throw new FeedbackSubmitError(MESSAGES.errorUnconfigured);
  if (!response.ok) throw new FeedbackSubmitError();

  const data = (await response.json().catch(() => null)) as Partial<FeedbackSubmitResult> | null;
  if (!data || typeof data.key !== 'string' || typeof data.url !== 'string') {
    throw new FeedbackSubmitError();
  }
  return typeof data.warning === 'string'
    ? { key: data.key, url: data.url, warning: data.warning }
    : { key: data.key, url: data.url };
}
